import type {
  ConfirmRequestSource,
  LoadConfirmEnvelope,
  SafetyClearResult,
  SafetyState,
  SystemMode,
  TrayLoad,
  TrayUnload,
  UnloadConfirmEnvelope,
} from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { ApiError } from '../errors.ts'
import { keys } from '../keys.ts'

/**
 * 알람 · 안전 · 운영 모드 · 적재/수거 확약.
 *
 * 🔴 **알람 자동 해제·자동 재개는 없다**(DP-02). 확인(ack)·해제(clear)는 항상 명시적 사용자 행위다.
 * ⚠ 적재·수거 확약 본문은 2026-09-26 실서버 스키마로 확인했다(`confirm_source` 필수). 나머지는 계약 기준이다.
 */

interface AlarmVars {
  alarmId: string
  note?: string
  commandId?: string
}

export const useAckAlarm = createCommand<AlarmVars, void>({
  send: ({ alarmId, note, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: `/alarms/${alarmId}/ack`,
      body: { command_id: commandId ?? newCommandId(), ...(note === undefined ? {} : { note }) },
      retryOn401: true,
    }),
  invalidates: ({ alarmId }) => [keys.alarms.detail(alarmId), keys.alarms.all()],
})

/** ⚠ latching 알람은 **유발 조건이 복귀(`resolved=true`)해야** 해제가 성립한다. 판정은 서버가 한다 */
export const useClearAlarm = createCommand<AlarmVars, void>({
  send: ({ alarmId, note, commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: `/alarms/${alarmId}/clear`,
      body: { command_id: commandId ?? newCommandId(), ...(note === undefined ? {} : { note }) },
      retryOn401: true,
    }),
  invalidates: ({ alarmId }) => [keys.alarms.detail(alarmId), keys.alarms.all()],
})

/** 일괄 해제 결과 — 성공한 id 와 실패한 id + 그 오류. 🔴 화면이 지어내지 않고 건별 응답을 그대로 모은다 */
export interface ClearAlarmsResult {
  cleared: string[]
  failed: { alarmId: string; error: ApiError }[]
}

/** 일괄 해제 동시 요청 수 — 서버에 몰아 보내지 않는다 */
export const CLEAR_ALARMS_CONCURRENCY = 4

/**
 * 알람 「모두 해제」(eric 2026-10-30 「알람 해제 기능을 넣어줘. 한번에.. 알람 없애게」).
 *
 * 🔴 **서버에 일괄 경로가 없다**(`openapi.json` 실측 — 알람 쓰기는 `POST /alarms/{id}/ack` · `/clear` 건별 둘).
 *    그래서 건별 `clear` 를 동시 {@link CLEAR_ALARMS_CONCURRENCY} 건까지 차례로 보낸다. 건마다 `command_id` 가 따로다.
 * 🔴 **한 건이 실패해도 멈추지 않고, 거부(reject)하지 않는다** — 결과에 성공 · 실패를 나눠 담는다.
 *    그래야 성공이 한 건이라도 있으면 `onSuccess` 의 무효화가 반드시 돈다(낙관적 갱신 없음 · DP-04).
 *    무효화는 끝난 뒤 **한 번**이다(건별 `useClearAlarm` 을 N 번 부르면 목록을 N 번 다시 받는다).
 * ⚠ 자동 해제가 아니다(DP-02) — 사람이 확인 창에서 누를 때만 부른다.
 */
export interface ClearAlarmsVars {
  alarmIds: readonly string[]
  note?: string
}

/** 일괄 해제의 실제 요청 — 훅 밖에서 시험할 수 있게 뗐다(`useClearAlarms` 가 이것을 부른다) */
export async function clearAlarms({ alarmIds, note }: ClearAlarmsVars): Promise<ClearAlarmsResult> {
  const result: ClearAlarmsResult = { cleared: [], failed: [] }
  let next = 0
  const worker = async () => {
    while (next < alarmIds.length) {
      const alarmId = alarmIds[next]
      next += 1
      if (alarmId === undefined) continue
      try {
        await orchestrator.request<void>({
          method: 'POST',
          path: `/alarms/${alarmId}/clear`,
          body: { command_id: newCommandId(), ...(note === undefined ? {} : { note }) },
          retryOn401: true,
        })
        result.cleared.push(alarmId)
      } catch (error) {
        result.failed.push({
          alarmId,
          error:
            error instanceof ApiError
              ? error
              : new ApiError({ status: 0, message: String(error), transport: false }),
        })
      }
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(CLEAR_ALARMS_CONCURRENCY, alarmIds.length) }, worker),
  )
  return result
}

export const useClearAlarms = createCommand<ClearAlarmsVars, ClearAlarmsResult>({
  send: clearAlarms,
  invalidates: () => [keys.alarms.all()],
})

/**
 * 비상 정지.
 * 🔴 **역할과 무관하게 항상 가능하다.** 게이트를 걸지 않는다 — 제어락도 권한도 묻지 않는다.
 */
export const useEmergencyStop = createCommand<{ commandId?: string }, void>({
  send: ({ commandId }) =>
    orchestrator.request<void>({
      method: 'POST',
      path: '/emergency-stop',
      body: { command_id: commandId ?? newCommandId() },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.safety.all(), keys.modules.all(), keys.batches.all()],
})

/**
 * 안전 해제 200 — `SafetyState` + 모듈별 fan-out 결과(계약 `clearSafety` 200 · 실물 2026-09-29
 * `{"state":"normal","cleared":true,…,"results":[{module_id, cleared, error_code}…]}`).
 * ⚠ 종전 이 훅은 응답을 `SafetyClearResult[]`(배열)로 적었다 — 실물은 객체다. 부르는 곳이 응답을 안 읽어 드러나지 않았다.
 */
export type SafetyClearResponse = SafetyState & { results?: SafetyClearResult[] }

/**
 * 안전 해제.
 * 🔴 **자동 재개가 아니다**(DP-02). 해제는 「물리 조건이 해소됐다」를 서버가 판정하고,
 *    거부되면 `unresolved_conditions` 가 온다. 화면이 그 목록을 그대로 보인다.
 */
export const useClearSafety = createCommand<{ commandId?: string }, SafetyClearResponse>({
  send: ({ commandId }) =>
    orchestrator.request<SafetyClearResponse>({
      method: 'POST',
      path: '/safety/clear',
      body: { command_id: commandId ?? newCommandId() },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.safety.all(), keys.modules.all()],
})

/** ⚠ `startup` 은 요청으로 설정할 수 없다(계약 enum 이 5종으로 좁다) */
export const useSetSystemMode = createCommand<
  { mode: Exclude<SystemMode['mode'], 'startup'>; commandId?: string },
  SystemMode
>({
  send: ({ mode, commandId }) =>
    orchestrator.request<SystemMode>({
      method: 'PUT',
      path: '/system/mode',
      body: { command_id: commandId ?? newCommandId(), mode },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.systemMode.all(), keys.modules.all()],
})

/**
 * 확약 창구 — **표면이 정한 값을 받아 싣는다**(`@ctcells/core` `CONFIRM_SOURCE_BY_SURFACE`).
 * 🔴 훅은 창구를 짐작하지 않는다. 필수 변수라 빠뜨리면 타입에서 걸린다.
 * ⚠ `dev_auto`(개발 모드 프로그램 확약)는 화면이 보내는 값이 아니라 타입에서 뺐다.
 */
type ScreenConfirmSource = Exclude<ConfirmRequestSource, 'dev_auto'>

/**
 * 적재 확약. 본문 = `command_id` + **`confirm_source`(필수)** + 선택 신고 칸(`loaded_chambers`).
 *
 * 🔴 **`confirm_source` 가 없으면 422 다**(2026-09-24 서버 신설 · 2026-09-26 실측).
 *    종전에는 서버가 늘 `tablet` 을 적어 데스크톱 확약도 태블릿으로 기록됐다.
 * 🔴 **챔버 신고(`loaded_chambers`)는 계약 본문에 있다**(실서버 `LoadConfirmEnvelope`). 화면의 적재
 *    확약 창(`LoadConfirmModal`)이 받고, **비운 칸은 인자에도 본문에도 없다** — 비우면 「신고하지 않는다」.
 *    값을 지어 `0` 으로 채우면 사람이 하지 않은 신고를 한 것이 된다.
 * ⚠ **평형추 신고(`counterweight_loaded`)는 계약에서 빠졌다**(2026-09-29 · 실서버 스키마가
 *   `command_id` · `confirm_source` · `loaded_chambers` 셋뿐). 서버가 422 없이 조용히 버리므로 싣지 않는다.
 *   ⚠ 본문을 조건부 spread 로 지으면 초과 속성 검사가 안 걸린다 — 타입이 막아 주지 않으니 시험이 막는다.
 * ⚠ 응답은 **`TrayLoad` 전문**이다 — 확약된 건을 다시 부르면 모듈을 찌르지 않고 현재 상태를
 *   에코한다(멱등). `void` 가 아니다.
 */
export const useConfirmLoad = createCommand<
  {
    loadId: string
    confirmSource: ScreenConfirmSource
    loadedChambers?: number
    commandId?: string
  },
  TrayLoad
>({
  send: ({ loadId, confirmSource, loadedChambers, commandId }) => {
    const body: LoadConfirmEnvelope = {
      command_id: commandId ?? newCommandId(),
      confirm_source: confirmSource,
      ...(loadedChambers === undefined ? {} : { loaded_chambers: loadedChambers }),
    }
    return orchestrator.request<TrayLoad>({
      method: 'POST',
      path: `/loads/${loadId}/confirm`,
      body,
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    })
  },
  invalidates: () => [keys.loads.all(), keys.batches.all()],
})

/**
 * 수거 확약. 적재와 거울상이고 **사유를 받지 않는다** — 본문 = `command_id` + `confirm_source`(필수)
 */
export const useConfirmUnload = createCommand<
  { unloadId: string; confirmSource: ScreenConfirmSource; commandId?: string },
  TrayUnload
>({
  send: ({ unloadId, confirmSource, commandId }) => {
    const body: UnloadConfirmEnvelope = {
      command_id: commandId ?? newCommandId(),
      confirm_source: confirmSource,
    }
    return orchestrator.request<TrayUnload>({
      method: 'POST',
      path: `/unloads/${unloadId}/confirm`,
      body,
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    })
  },
  invalidates: () => [keys.unloads.all(), keys.batches.all()],
})

/** 안전 상태를 캐시에 반영하는 쪽은 구독이다. 여기 타입은 명령 응답 재사용을 위해 남긴다 */
export type { SafetyState }
